import React from 'react';
import { Card, CardContent } from './ui/card';
import { CheckCircle2, Users, BookOpen, Heart, GraduationCap, Award, Sprout, Target } from 'lucide-react';

export const WhyDoctrina = () => {
  const benefits = [
    {
      icon: Users,
      title: 'Small Class Sizes',
      description: 'Classes of 30 or fewer students for personalized attention and meaningful relationships.'
    },
    {
      icon: Heart,
      title: 'Whole Child Focus',
      description: 'Academic excellence balanced with social-emotional learning, creativity, and character development.'
    },
    {
      icon: BookOpen,
      title: 'Personalized Learning',
      description: 'Individualized learning plans tailored to each student\'s strengths, needs, and goals.'
    },
    {
      icon: Sprout,
      title: 'Culturally Responsive',
      description: 'Deep respect for diverse backgrounds with programs honoring the Inland Empire\'s rich cultural heritage.'
    },
    {
      icon: GraduationCap,
      title: 'College & Career Ready',
      description: 'Rigorous academics preparing students for high school success and future opportunities.'
    },
    {
      icon: Award,
      title: 'Experienced Educators',
      description: 'Passionate, highly qualified teachers empowered to innovate and lead.'
    },
    {
      icon: Target,
      title: 'Real-World Learning',
      description: 'Project-based curriculum connecting classroom lessons to community challenges.'
    },
    {
      icon: CheckCircle2,
      title: 'Free Public Education',
      description: 'No tuition, open enrollment with nondiscriminatory admissions through public lottery.'
    }
  ];

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-16">
          <div className="inline-block bg-coralRed/10 px-6 py-2 rounded-full mb-4">
            <span className="text-coralRed font-semibold">Why Choose Doctrina</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-navyBlue mb-6">What Sets Us Apart</h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Discover the unique advantages that make Doctrina Charter School the ideal choice for your child's middle school years.
          </p>
        </div>

        {/* Shop Class Image */}
        <div className="mb-16 relative rounded-3xl overflow-hidden shadow-2xl max-h-[400px]">
          <img 
            src={process.env.REACT_APP_SHOP_CLASS_URL} 
            alt="Students learning hands-on skills in shop class" 
            className="w-full h-full object-cover" style={{maxHeight: '400px'}}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navyBlue/70 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12 text-white">
            <h3 className="text-3xl sm:text-4xl font-bold mb-3">Hands-On Learning & Career Skills</h3>
            <p className="text-lg sm:text-xl text-white/90 max-w-3xl">
              Our comprehensive shop and trade programs equip students with practical skills in woodworking, construction, and technical arts—preparing them for college and career pathways.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {benefits.slice(0, 4).map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <Card key={index} className="border-none shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white group">
                <CardContent className="p-6">
                  <div className="bg-tealAccent/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:bg-tealAccent/20 transition-colors duration-300">
                    <Icon className="text-tealAccent" size={24} />
                  </div>
                  <h3 className="text-lg font-bold text-navyBlue mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Robotics Class Image */}
        <div className="mb-16 relative rounded-3xl overflow-hidden shadow-2xl max-h-[400px]">
          <img 
            src={process.env.REACT_APP_ROBOTICS_CLASS_URL} 
            alt="Students programming and building robots" 
            className="w-full h-full object-cover" style={{maxHeight: '400px'}}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-tealAccent/70 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12 text-white">
            <h3 className="text-3xl sm:text-4xl font-bold mb-3">STEM Excellence & Innovation</h3>
            <p className="text-lg sm:text-xl text-white/90 max-w-3xl">
              Students explore coding, robotics, and artificial intelligence through Code.org and hands-on projects—developing critical thinking and problem-solving skills for the future.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {benefits.slice(4, 8).map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <Card key={index} className="border-none shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white group">
                <CardContent className="p-6">
                  <div className="bg-tealAccent/10 w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:bg-tealAccent/20 transition-colors duration-300">
                    <Icon className="text-tealAccent" size={24} />
                  </div>
                  <h3 className="text-lg font-bold text-navyBlue mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Festival Image */}
        <div className="mb-16 relative rounded-3xl overflow-hidden shadow-2xl max-h-[400px]">
          <img 
            src={process.env.REACT_APP_FESTIVAL_URL} 
            alt="Students and families celebrating at school festival" 
            className="w-full h-full object-cover" style={{maxHeight: '400px'}}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-coralRed/70 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12 text-white">
            <h3 className="text-3xl sm:text-4xl font-bold mb-3">Vibrant Community & Celebrations</h3>
            <p className="text-lg sm:text-xl text-white/90 max-w-3xl">
              Our school festivals, performances, and cultural celebrations bring together students, families, and the community—creating lasting memories and strong bonds.
            </p>
          </div>
        </div>

        {/* For Different Stakeholders */}
        <div className="grid md:grid-cols-3 gap-8">
          {/* For Families */}
          <div className="bg-gradient-to-br from-tealAccent/10 to-white rounded-2xl p-8 border-2 border-tealAccent/20">
            <h3 className="text-2xl font-bold text-navyBlue mb-4">For Families</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="text-tealAccent flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700">Supportive, inclusive environment where every child belongs</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="text-tealAccent flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700">Strong home-school partnerships with monthly workshops</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="text-tealAccent flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700">Extended campus hours and family education programs</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="text-tealAccent flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700">Transparent communication and parent advisory council</span>
              </li>
            </ul>
          </div>

          {/* For Community */}
          <div className="bg-gradient-to-br from-sageGreen/10 to-white rounded-2xl p-8 border-2 border-sageGreen/20">
            <h3 className="text-2xl font-bold text-navyBlue mb-4">For Community</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="text-sageGreen flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700">Local learning hub serving Inland Empire families</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="text-sageGreen flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700">Partnership opportunities with businesses and nonprofits</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="text-sageGreen flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700">Student community service and leadership development</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="text-sageGreen flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700">Adult education programs and community events</span>
              </li>
            </ul>
          </div>

          {/* For Donors/Board */}
          <div className="bg-gradient-to-br from-goldenOlive/10 to-white rounded-2xl p-8 border-2 border-goldenOlive/20">
            <h3 className="text-2xl font-bold text-navyBlue mb-4">For Partners</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="text-goldenOlive flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700">Innovative educational model with proven outcomes</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="text-goldenOlive flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700">Strong governance and financial transparency</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="text-goldenOlive flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700">Measurable student outcomes and accountability</span>
              </li>
              <li className="flex items-start space-x-2">
                <CheckCircle2 className="text-goldenOlive flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700">Investment in underserved Inland Empire communities</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyDoctrina;
